import React, { useState } from 'react';
import { Sidebar } from './components/Sidebar';
import { ApiSidebar } from './components/ApiSidebar';
import { CheckoutSidebar } from './components/CheckoutSidebar';
import { TopNav } from './components/TopNav';
import { Footer } from './components/Footer';
// Documentation Pages
import { OverviewPage } from './pages/OverviewPage';
import { GettingStartedPage } from './pages/GettingStartedPage';
import { HostedSessionPage } from './pages/HostedSessionPage';
import { DirectPaymentPage } from './pages/DirectPaymentPage';
import { BatchIntegrationPage } from './pages/BatchIntegrationPage';
import { MobileIntegrationPage } from './pages/MobileIntegrationPage';
import { ShoppingCartPage } from './pages/ShoppingCartPage';
import { PaymentMethodsPage } from './pages/PaymentMethodsPage';
import { SecurityPage } from './pages/SecurityPage';
import { FeaturesPage } from './pages/FeaturesPage';
import { ServerApiPage } from './pages/ServerApiPage';
import { MultipleSourcesPage } from './pages/MultipleSourcesPage';
import { JavaScriptLibsPage } from './pages/JavaScriptLibsPage';
import { AnnouncementsPage } from './pages/AnnouncementsPage';
import { GlossaryPage } from './pages/GlossaryPage';
// Checkout Sub-Pages
import { CheckoutOverviewPage } from './pages/checkout/CheckoutOverviewPage';
import { CheckoutIntegrationStepsPage } from './pages/checkout/CheckoutIntegrationStepsPage';
import { CheckoutSessionPage } from './pages/checkout/CheckoutSessionPage';
import { CheckoutHostedPaymentPage } from './pages/checkout/CheckoutHostedPaymentPage';
import { CheckoutSubsequentOpsPage } from './pages/checkout/CheckoutSubsequentOpsPage';
import { CheckoutResponsePage } from './pages/checkout/CheckoutResponsePage';
import { CheckoutCustomizingPage } from './pages/checkout/CheckoutCustomizingPage';
import { CheckoutFeaturesPage } from './pages/checkout/CheckoutFeaturesPage';
import { CheckoutBrowsersPage } from './pages/checkout/CheckoutBrowsersPage';
import { CheckoutTestingPage } from './pages/checkout/CheckoutTestingPage';
import { CheckoutGoLivePage } from './pages/checkout/CheckoutGoLivePage';
// API Reference Pages
import { ApiOverviewPage } from './pages/ApiOverviewPage';
import { ApiTransactionPage } from './pages/ApiTransactionPage';
import { ApiAgreementPage } from './pages/api/ApiAgreementPage';
import { ApiAuthenticationPage } from './pages/api/ApiAuthenticationPage';
import { ApiBatchPage } from './pages/api/ApiBatchPage';
import { ApiBrowserPaymentPage } from './pages/api/ApiBrowserPaymentPage';
import { ApiGatewayPage } from './pages/api/ApiGatewayPage';
import { ApiHostedCheckoutPage } from './pages/api/ApiHostedCheckoutPage';
import { ApiPaymentPlanPage } from './pages/api/ApiPaymentPlanPage';
import { ApiSessionPage } from './pages/api/ApiSessionPage';
import { ApiRiskAssessmentPage } from './pages/api/ApiRiskAssessmentPage';
import { ApiTokenizationPage } from './pages/api/ApiTokenizationPage';
import { ApiWalletPage } from './pages/api/ApiWalletPage';
import { SdkCheckoutPage } from './pages/api/SdkCheckoutPage';
import { SdkClickToPayPage } from './pages/api/SdkClickToPayPage';
import { SdkPaypalPage } from './pages/api/SdkPaypalPage';
import { SdkRiskPage } from './pages/api/SdkRiskPage';
import { SdkSessionPage } from './pages/api/SdkSessionPage';
export function App() {
  const [activeSection, setActiveSection] = useState<'documentation' | 'api-reference'>('documentation');
  const [activeDocPage, setActiveDocPage] = useState('overview');
  const [activeApiPage, setActiveApiPage] = useState('api-overview');
  const handleSectionChange = (section: 'documentation' | 'api-reference') => {
    setActiveSection(section);
    const mainEl = document.getElementById('main-content');
    if (mainEl) mainEl.scrollTop = 0;
  };
  const handleDocNavigate = (pageId: string) => {
    setActiveDocPage(pageId);
    const mainEl = document.getElementById('main-content');
    if (mainEl) mainEl.scrollTop = 0;
  };
  const handleApiNavigate = (pageId: string) => {
    setActiveApiPage(pageId);
    const mainEl = document.getElementById('main-content');
    if (mainEl) mainEl.scrollTop = 0;
  };
  const isCheckoutSection = activeDocPage === 'checkout' || activeDocPage.startsWith('checkout-');
  const renderDocContent = () => {
    switch (activeDocPage) {
      case 'overview':
        return <OverviewPage onNavigate={handleDocNavigate} onSectionChange={handleSectionChange} data-id="element-0" />;
      case 'getting-started':
        return <GettingStartedPage data-id="element-1" />;
      // Checkout sub-pages
      case 'checkout':
        return <CheckoutOverviewPage onNavigate={handleDocNavigate} data-id="element-2" />;
      case 'checkout-integration-steps':
        return <CheckoutIntegrationStepsPage onNavigate={handleDocNavigate} data-id="element-3" />;
      case 'checkout-session':
        return <CheckoutSessionPage onNavigate={handleDocNavigate} data-id="element-4" />;
      case 'checkout-hosted-payment-page':
        return <CheckoutHostedPaymentPage onNavigate={handleDocNavigate} data-id="element-5" />;
      case 'checkout-subsequent-ops':
        return <CheckoutSubsequentOpsPage onNavigate={handleDocNavigate} data-id="element-6" />;
      case 'checkout-response':
        return <CheckoutResponsePage onNavigate={handleDocNavigate} data-id="element-7" />;
      case 'checkout-customizing':
        return <CheckoutCustomizingPage onNavigate={handleDocNavigate} data-id="element-8" />;
      case 'checkout-features':
        return <CheckoutFeaturesPage onNavigate={handleDocNavigate} data-id="element-9" />;
      case 'checkout-browsers':
        return <CheckoutBrowsersPage onNavigate={handleDocNavigate} data-id="element-10" />;
      case 'checkout-testing':
        return <CheckoutTestingPage onNavigate={handleDocNavigate} data-id="element-11" />;
      case 'checkout-go-live':
        return <CheckoutGoLivePage onNavigate={handleDocNavigate} data-id="element-12" />;
      // Other doc pages
      case 'hosted-session':
        return <HostedSessionPage data-id="element-13" />;
      case 'direct-payment':
        return <DirectPaymentPage data-id="element-14" />;
      case 'batch-integration':
        return <BatchIntegrationPage data-id="element-15" />;
      case 'mobile-integration':
        return <MobileIntegrationPage data-id="element-16" />;
      case 'shopping-cart':
        return <ShoppingCartPage data-id="element-17" />;
      case 'payment-methods':
        return <PaymentMethodsPage data-id="element-18" />;
      case 'security':
        return <SecurityPage data-id="element-19" />;
      case 'features':
        return <FeaturesPage data-id="element-20" />;
      case 'server-api':
        return <ServerApiPage data-id="element-21" />;
      case 'multiple-sources':
        return <MultipleSourcesPage data-id="element-22" />;
      case 'js-libraries':
        return <JavaScriptLibsPage data-id="element-23" />;
      case 'announcements':
        return <AnnouncementsPage data-id="element-24" />;
      case 'glossary':
        return <GlossaryPage data-id="element-25" />;
      default:
        return <OverviewPage onNavigate={handleDocNavigate} onSectionChange={handleSectionChange} data-id="element-26" />;
    }
  };
  const renderApiContent = () => {
    switch (activeApiPage) {
      case 'api-overview':
        return <ApiOverviewPage onNavigate={handleApiNavigate} data-id="element-27" />;
      case 'api-agreement':
      case 'api-agreement-create':
      case 'api-agreement-update':
      case 'api-agreement-retrieve':
      case 'api-agreement-delete':
        return <ApiAgreementPage onNavigate={handleApiNavigate} data-id="element-28" />;
      case 'api-authentication':
      case 'api-auth-initiate':
      case 'api-auth-authenticate':
        return <ApiAuthenticationPage data-id="element-29" />;
      case 'api-batch':
      case 'api-batch-submit':
      case 'api-batch-retrieve':
        return <ApiBatchPage data-id="element-30" />;
      case 'api-browser-payment':
      case 'api-bp-initiate':
      case 'api-bp-confirm':
      case 'api-bp-retrieve':
        return <ApiBrowserPaymentPage data-id="element-31" />;
      case 'api-gateway':
      case 'api-gateway-info':
      case 'api-gateway-options':
      case 'api-gateway-merchant':
        return <ApiGatewayPage data-id="element-32" />;
      case 'api-hosted-checkout':
      case 'api-hc-create':
      case 'api-hc-retrieve':
        return <ApiHostedCheckoutPage data-id="element-33" />;
      case 'api-payment-plan':
      case 'api-pp-create':
      case 'api-pp-update':
      case 'api-pp-retrieve':
        return <ApiPaymentPlanPage data-id="element-34" />;
      case 'api-session':
      case 'api-session-create':
      case 'api-session-update':
      case 'api-session-retrieve':
        return <ApiSessionPage data-id="element-35" />;
      case 'api-risk':
      case 'api-risk-evaluate':
        return <ApiRiskAssessmentPage data-id="element-36" />;
      case 'api-tokenization':
      case 'api-token-create':
      case 'api-token-update':
      case 'api-token-delete':
      case 'api-token-retrieve':
        return <ApiTokenizationPage data-id="element-37" />;
      case 'api-transaction':
      case 'api-txn-pay':
      case 'api-txn-auth':
      case 'api-txn-capture':
      case 'api-txn-refund':
      case 'api-txn-void':
      case 'api-txn-verify':
      case 'api-txn-retrieve':
        return <ApiTransactionPage onNavigate={handleApiNavigate} data-id="element-38" />;
      case 'api-wallet':
      case 'api-wallet-create':
      case 'api-wallet-update':
        return <ApiWalletPage data-id="element-39" />;
      case 'api-sdk-checkout':
        return <SdkCheckoutPage data-id="element-40" />;
      case 'api-sdk-clicktopay':
        return <SdkClickToPayPage data-id="element-41" />;
      case 'api-sdk-paypal':
        return <SdkPaypalPage data-id="element-42" />;
      case 'api-sdk-risk':
        return <SdkRiskPage data-id="element-43" />;
      case 'api-sdk-session':
        return <SdkSessionPage data-id="element-44" />;
      default:
        return <ApiOverviewPage onNavigate={handleApiNavigate} data-id="element-45" />;
    }
  };
  const renderSidebar = () => {
    if (activeSection === 'api-reference') {
      return <ApiSidebar activePage={activeApiPage} onNavigate={handleApiNavigate} data-id="element-46" />;
    }
    if (isCheckoutSection) {
      return <CheckoutSidebar activePage={activeDocPage} onNavigate={handleDocNavigate} data-id="element-47" />;
    }
    return <Sidebar activePage={activeDocPage} onNavigate={handleDocNavigate} data-id="element-48" />;
  };
  return <div className="min-h-screen flex bg-white font-sans text-gray-900" data-id="element-49">
      {renderSidebar()}
      <div className="flex-1 flex flex-col min-w-0 h-screen overflow-hidden bg-white" data-id="element-50">
        <TopNav activeSection={activeSection} onSectionChange={handleSectionChange} data-id="element-51" />
        <main id="main-content" className="flex-1 overflow-y-auto flex flex-col" data-id="element-52">
          <div className="flex-1" data-id="element-53">
            {activeSection === 'documentation' ? renderDocContent() : renderApiContent()}
          </div>
          <Footer data-id="element-54" />
        </main>
      </div>
    </div>;
}